import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import './estilos/App.css';
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Axios,
  setToken,
  deleteToken,
  getToken,
  initAxiosInterceptors,
  performHardReset,
  clearAuthCookies
} from './utils/auth-helpers';
import { Router, Switch, Route } from 'react-router-dom';
import 'react-notifications-component/dist/theme.css';
import "./estilos/general.css";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import history from './history';
import { LoadingProvider } from './contextos/LoadingContext';
import GlobalSpinner from './componentes/GlobalSpinner';
import { LogoProvider, useLogo } from './contextos/LogoContext';
import { generarRutasDesdeMenu, validarMenuItems } from './utils/generadorRutas';
import { limpiarCacheCondiciones } from './servicios/menuService';
import './utils/debugAuth'; // Script de debug de autenticación

// Lazy load para páginas - Solo componentes esenciales
const Login = lazy(() => import("./paginas/usuarios/login"));
const PiePagina = lazy(() => import('./paginas/menu/piepagina'));
const MainLayout = lazy(() => import('./paginas/menu/MainLayout'));
const Dashboard = lazy(() => import('./paginas/menu/dashboard'));
const PaginaResultados = lazy(() => import('./paginas/busqueda/PaginaResultados'));
const ResetPassword = lazy(() => import('./paginas/usuarios/resetPassword'));
const RecuperarPassword = lazy(() => import('./paginas/usuarios/recuperarPassword'));
const CambioClave = lazy(() => import("./paginas/usuarios/cambioClave"));


initAxiosInterceptors();

function App() {
  const [usuario, setUsuario] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(false);
  const [titulo, setTitulo] = useState(null);
  const [showMessage, setShowMessage] = useState(false);
  const [toggleMenu, setToggleMenu] = useState(false); 
  const [logoSistema, setLogoSistema] = useState(null);
  const [refreshMenu, setRefreshMenu] = useState(false);
  const location = useLocation();

  useEffect(() => {
    async function cargarLogo() {
      try {
        const token = getToken();
        if (!token) return;
        const { data } = await Axios.get('/api/v1/preferencias/', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const prefs = data.result || [];
        const logoPref = prefs.find(p => p.titulo === 'logoSistema');
        if (logoPref && logoPref.valor) {
          setLogoSistema(logoPref.valor);
        }
      } catch (error) {
        console.error('No se pudo cargar el logo del sistema', error);
      }
    }
    
      async function cargarUsuarioInicial() {
	  const tokenActual = getToken();
	  if (!tokenActual) return;
	  
	  try {
	      const { data } = await Axios.get('/api/v1/autenticacion/whoami');
	      if (data.token) {
		  localStorage.setItem('token', data.token); 
		  Axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
	      }

	      setUsuario(data.result);
	      
	      await cargarLogo();
	      
	      if (items.length === 0) {
		  await getMenus();
	      }
	      
	  } catch (error) {
	      console.error("Error cargando usuario inicial:", error);
	  }
      }      
    
    // Solo ejecutar al montar el componente
    cargarUsuarioInicial();
    
    // Manejar colapso del menú en POS
    if (location.pathname === "/pos") {
      setToggleMenu(false);
    }
  }, []); // Array vacío = solo se ejecuta al montar

    async function getMenus() {
  setLoadingMenus(true);
  const token = getToken();
  const options = {
      headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
      },
  };

  try {
      const res = await Axios.get(`/api/v1/navegacion/`, options);
      const data = res.data?.result ?? [];
      
      setItems(data);
      
      if (refreshMenu) {
        setRefreshMenu(false);
      }
      
      return data;
  } catch (error) {
      console.error("Error al obtener los menús:", error);
      setItems([]);
      return [];
  } finally {
      setLoadingMenus(false);
  }
    }

  // Función para refrescar menús tras operaciones CRUD
  const handleRefreshMenu = useCallback(() => {
    // Forzar el refresh
    setRefreshMenu(true);
    
    // Llamar getMenus de manera asíncrona para asegurar que refreshMenu esté set
    setTimeout(() => {
      getMenus();
    }, 0);
  }, []);

  async function login(email, password) {
    try {
      // SECURITY: Asegurar limpieza completa antes de iniciar sesión
      // Aunque ya se ejecuta en el useEffect del Login, lo hacemos aquí también
      // como medida de seguridad adicional
      performHardReset();
      
      // CRÍTICO: Limpiar estado de React antes de la petición
      setItems([]); // Limpiar menús del usuario anterior
      limpiarCacheCondiciones(); // Limpiar caché de condiciones
      
      // Realizar petición de login SIN headers Authorization heredados
      // El interceptor de Axios ya se encarga de eliminar Authorization para /autenticacion/iniciar
      const { data } = await Axios.post('/api/v1/autenticacion/iniciar', {
        correo: email,
        clave: password,
      });
  
      if (data.logged) {
        // Establecer nuevo usuario y token
        setUsuario(data.result.usuario);
        setToken(data.result.access_token);
        
        // Cargar menús del NUEVO usuario
        getMenus();
      } else {
        mostrarMensaje('danger', data.result.title, data.result.message);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function logout() {
    try {
      // CRÍTICO: Expirar las cookies JWT en el backend (son HttpOnly)
      await clearAuthCookies();
    } finally {
      // Limpieza local garantizada aunque falle el endpoint
      performHardReset();
      setUsuario(null);
      setItems([]); // Limpiar menús al cerrar sesión
      limpiarCacheCondiciones(); // Limpiar caché de condiciones
      deleteToken();

      // CRÍTICO: Forzar recarga completa del navegador para limpiar TODO el estado
      // Esto previene que datos del usuario anterior persistan en memoria
      window.location.href = '/';
    }
  }

  function showMenu() {
    setToggleMenu(!toggleMenu);
  }

  function mostrarMensaje(type, title, message) {
    setShowMessage(true);
    const content = (
      <>
        <strong>{title}</strong>
        <div>{message}</div>
      </>
    );
    switch (type) {
      case "success":
        toast.success(content, {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          onClose: () => setShowMessage(false)
        });
        break;
      case "error":
      case "danger":
        toast.error(content, {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          onClose: () => setShowMessage(false)
        });
        break;
      case "info":
        toast.info(content, {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          onClose: () => setShowMessage(false)
        });
        break;
      case "warning":
        toast.warn(content, {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          onClose: () => setShowMessage(false)
        });
        break;
      default:
        toast(content, {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          onClose: () => setShowMessage(false)
        });
        break;
    }
  }

  function setTitle(title) {
    setTitulo(title);
  }

  return (
    <LogoProvider>
      <BrandingEffect />
      <LoadingProvider>
        <ToastContainer />
        <GlobalSpinner />
        <Router history={history}>
          <Suspense fallback={<div style={{ padding: 40, textAlign: "center" }}>Cargando...</div>}>
            {usuario ? (
              <>
                <WrapperApp
                  usuario={usuario}
                  logout={logout}
                  items={items}
                  loadingMenus={loadingMenus}
                  toggleMenu={toggleMenu}
                  showMenu={showMenu}
                  titulo={titulo}
                  setTitle={setTitle}
                  showMessage={showMessage}
                  mostrarMensaje={mostrarMensaje}
                  logoSistema={logoSistema}
                  menuItems={items}
                  handleRefreshMenu={handleRefreshMenu}
                />
              </>
            ) : (
              <LogoutRoutes login={login} logoSistema={logoSistema} />
            )}
          </Suspense>
        </Router>
      </LoadingProvider>
    </LogoProvider>
  );

// Componente para aplicar título y favicon dinámicos
function BrandingEffect() {
  const { title, faviconUrl } = useLogo();
  React.useEffect(() => {
    if (title) {
      document.title = title;
    }
    if (faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
    }
  }, [title, faviconUrl]);
  return null;
}
}

function WrapperApp({ usuario, logout, items, loadingMenus, toggleMenu, showMenu, titulo, setTitle, mostrarMensaje, logoSistema, menuItems, handleRefreshMenu }) {
  const location = useLocation();
  const isMobile = window.innerWidth <= 900;
  const isPos = location.pathname === "/pos";
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const sidebarOpen = (!isMobile && !isPos) || toggleMenu;

  // Validar compatibilidad del menú con componentes disponibles
  useEffect(() => {
    if (menuItems && menuItems.length > 0) {
      const estadisticas = validarMenuItems(menuItems);
      
      if (estadisticas.invalidas > 0) {
        console.warn('[Rutas Inválidas] Items de menú sin componente:', estadisticas.itemsInvalidos);
      }
    }
  }, [menuItems]);

  useEffect(() => {
    function findTitle(items, path) {
      for (const item of items) {
        if (item.url === path) return item.nombre;
        if (item.items) {
          const found = findTitle(item.items, path);
          if (found) return found;
        }
      }
      return null;
    }
    const newTitle = findTitle(items, location.pathname);
    if (newTitle) setTitle(newTitle);
    else setTitle(""); 
  }, [location.pathname, items, setTitle]);
    
  return (
    <MainLayout
      usuario={usuario}
      items={items}
      loadingMenus={loadingMenus}
      titulo={titulo}
      setTitle={setTitle}
      logout={logout}
      showMenu={showMenu}
      sidebarOpen={sidebarOpen}
      mobileOpen={mobileOpen}
      setMobileOpen={setMobileOpen}
      logoSistema={logoSistema}
    >
      <LoginRoutes 
        usuario={usuario} 
        logout={logout} 
        showMenu={showMenu} 
        mostrarMensaje={mostrarMensaje}
        menuItems={menuItems}
        onRefreshMenu={handleRefreshMenu}
      />
      <PiePagina logoSistema={logoSistema} />
    </MainLayout>
  );
}

function LoginRoutes({ usuario, logout, showMenu, mostrarMensaje, menuItems, onRefreshMenu }) {
  
  // Estado de carga inicial: si no hay menuItems pero hay usuario, aún está cargando
  const isLoading = usuario && (!menuItems || menuItems.length === 0);
  
  // Generar rutas dinámicamente desde el menú
  const rutasDinamicas = generarRutasDesdeMenu(menuItems || [], {
    usuario,
    logout,
    showMenu,
    mostrarMensaje,
    datosUsuarioActual: usuario,
    onRefreshMenu: onRefreshMenu
  });

  return (
    <Switch>
      {/* Ruta principal - Dashboard o Resultados de búsqueda */}
      <Route exact path="/" render={props => {
        const urlParams = new URLSearchParams(props.location.search);
        const searchQuery = urlParams.get('q');
        
        if (searchQuery) {
          return <PaginaResultados {...props} menuItems={menuItems || []} />;
        }
        return <Dashboard {...props} usuario={usuario} salir={logout} />;
      }} />
      
      {/* Página de resultados de búsqueda */}
      <Route path="/busqueda" render={props => <PaginaResultados {...props} menuItems={menuItems || []} />} />
      
      {/* Rutas estáticas críticas */}
      <Route path="/recuperarpassword" render={props => <RecuperarPassword {...props} />} />
      <Route path="/resetpassword" render={props => <ResetPassword {...props} mostrarMensaje={mostrarMensaje} />} />
      <Route
        path="/cambioclave/:usuarioId"
        render={props => <CambioClave {...props} mostrarMensaje={mostrarMensaje} />}
      />
      
      {/* Rutas dinámicas generadas desde el menú */}
      {rutasDinamicas}
      
      {/* Ruta 404 - Solo mostrar si NO está cargando */}
      <Route render={() => {
        if (isLoading) {
          return (
            <div style={{ 
              padding: 40, 
              textAlign: "center", 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh'
            }}>
              <div className="spinner-border" role="status" style={{ marginBottom: 16 }}></div>
              <div style={{ color: '#666' }}>Cargando sistema de menús...</div>
            </div>
          );
        }
        return (
          <div style={{ padding: 40, textAlign: "center" }}>
            <h3>Página no encontrada</h3>
            <p>La ruta solicitada no existe en el sistema.</p>
          </div>
        );
      }} />
    </Switch>
  );
}

function LogoutRoutes({ login, logoSistema }) {
  return (
    <Switch>
      <Route exact path="/" render={props => <Login {...props} login={login} logoSistema={logoSistema} />} />
      <Route path="/recuperarpassword" render={props => <RecuperarPassword {...props} logoSistema={logoSistema} />} />
      <Route path="/resetpassword" render={props => <ResetPassword {...props} logoSistema={logoSistema} />} />
    </Switch>
  );
}

		  
export default App;
